import { useCallback, useEffect, useRef, useState } from 'react';
import { useStore } from '@/stores';
import type { AgentDefinition } from '@/api/types';
import { getAgentsByIds, searchAgents, type AgentSearchParams } from '@/api/agents';

// THE AGENT DIRECTORY, A PAGE AT A TIME (2 Nov 2026). A practice has more agents than one page (256 at practice 100), so
// nothing in the Studio lists or looks agents up in a page it happens to hold: the SERVER searches, filters and orders
// (api/Agent/Search → AIAgentConfigManager.SearchDirectoryAsync), and every picker, link drawer and the command palette
// asks through this hook. The text matches the names, canonical name, description and id, never the instruction body.

/** Typing waits this long before asking the server, so a word costs one request, not one per letter. */
export const TYPING_PAUSE_MS = 250;

export interface AgentDirectoryPage {
  rows: AgentDefinition[];
  /** The server's count for this search and these filters; null before the first answer and after an error. */
  total: number | null;
  /** The first page is on its way (the rows shown, if any, are the previous request's). */
  loading: boolean;
  loadingMore: boolean;
  /** The server's own words, e.g. a filter word it refused. */
  error: string | null;
  /** The server has more rows than are shown. */
  more: boolean;
  /** Asks for the next page of the same request and adds it. */
  showMore: () => void;
}

/**
 * Asks the directory for `request`, page 1, whenever it changes (by value: a caller may build it every render), after a
 * typing pause when it carries text. `null` asks nothing and clears. An answer for an older request is dropped.
 */
export function useAgentDirectory(request: AgentSearchParams | null): AgentDirectoryPage {
  const [rows, setRows] = useState<AgentDefinition[]>([]);
  const [total, setTotal] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** Bumps on every new request; an answer for an older one is dropped. */
  const seq = useRef(0);
  const page = useRef(1);
  const current = useRef<AgentSearchParams | null>(null);
  const key = request == null ? null : JSON.stringify(request);

  useEffect(() => {
    const mine = ++seq.current;
    current.current = request;
    page.current = 1;
    setLoadingMore(false);
    if (request == null) {
      setRows([]);
      setTotal(null);
      setError(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    const t = setTimeout(() => {
      searchAgents({ ...request, pageNo: 1 })
        .then((res) => {
          if (mine !== seq.current) return;
          setRows(res.data ?? []);
          setTotal(res.paging?.totalRecordCount ?? res.data?.length ?? 0);
        })
        .catch((err) => {
          if (mine !== seq.current) return;
          setRows([]);
          setTotal(null);
          setError(err instanceof Error ? err.message : 'Failed to load agents');
        })
        .finally(() => {
          if (mine === seq.current) setLoading(false);
        });
    }, request.search ? TYPING_PAUSE_MS : 0);
    return () => clearTimeout(t);
    // `key` is the request by value.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  const showMore = useCallback(() => {
    const req = current.current;
    if (req == null) return;
    const mine = seq.current;
    const next = page.current + 1;
    setLoadingMore(true);
    searchAgents({ ...req, pageNo: next })
      .then((res) => {
        if (mine !== seq.current) return;
        page.current = next;
        setRows((r) => [...r, ...(res.data ?? [])]);
      })
      .catch((err) => {
        if (mine === seq.current) setError(err instanceof Error ? err.message : 'Failed to load agents');
      })
      .finally(() => {
        if (mine === seq.current) setLoadingMore(false);
      });
  }, []);

  return { rows, total, loading, loadingMore, error, more: total != null && rows.length < total, showMore };
}

/**
 * One agent's directory row by id, for a label or a card: `known` (the row a picker just handed back) answers without
 * asking; otherwise the server is asked for it (in the selected practice or shared by every practice). Undefined until
 * it is known, and for an id the directory does not hold, so a caller falls back to "Agent #id".
 */
export function useAgentRow(id: number | null | undefined, known?: AgentDefinition | null): AgentDefinition | undefined {
  const { merchantContextStore } = useStore();
  const merchantId = merchantContextStore.selectedMerchantId;
  const [fetched, setFetched] = useState<AgentDefinition | undefined>();
  const hit = id != null && known?.id === id ? known : undefined;

  useEffect(() => {
    if (id == null || hit || merchantId == null || fetched?.id === id) return;
    let cancelled = false;
    getAgentsByIds(merchantId, [id])
      .then((found) => {
        if (!cancelled) setFetched(found.find((a) => a.id === id));
      })
      .catch(() => undefined); // the label falls back to "Agent #id"
    return () => {
      cancelled = true;
    };
  }, [id, hit, merchantId, fetched]);

  if (id == null) return undefined;
  return hit ?? (fetched?.id === id ? fetched : undefined);
}
